/**
 * useGenerationForm — GenerateModal 공통 폼 상태
 */

import { useState, useCallback } from 'react'
import type { PersonRole } from '@cugumi/shared/types'
import type { PromptBlockSelection } from '@cugumi/shared/schemas'
import { useReferencePhotos } from './useReferencePhotos'

export function useGenerationForm(defaultRole: PersonRole = 'GROOM') {
  const { groomPhotos, bridePhotos, upload, uploading } = useReferencePhotos()

  const [role, setRole] = useState<PersonRole>(defaultRole)
  const [selectedGroomId, setSelectedGroomId] = useState<string | null>(null)
  const [selectedBrideId, setSelectedBrideId] = useState<string | null>(null)
  const [blockSelection, setBlockSelection] = useState<Partial<PromptBlockSelection>>({})

  const hasGroom = groomPhotos.length > 0
  const hasBride = bridePhotos.length > 0

  const isRefValid =
    (role === 'GROOM' && !!selectedGroomId) ||
    (role === 'BRIDE' && !!selectedBrideId) ||
    (role === 'COUPLE' && !!selectedGroomId && !!selectedBrideId)

  const isBlocksValid =
    !!blockSelection.styleSlug &&
    !!blockSelection.poseSlug &&
    !!blockSelection.wardrobeSlug &&
    !!blockSelection.cameraSlug

  const isFormValid = isRefValid && isBlocksValid

  /** 선택된 참조 사진 URL 수집 */
  const getSelectedImageUrls = useCallback(() => {
    const urls: string[] = []
    if (role === 'GROOM' || role === 'COUPLE') {
      const photo = groomPhotos.find((p) => p.id === selectedGroomId)
      if (photo) urls.push(photo.originalUrl)
    }
    if (role === 'BRIDE' || role === 'COUPLE') {
      const photo = bridePhotos.find((p) => p.id === selectedBrideId)
      if (photo) urls.push(photo.originalUrl)
    }
    return urls
  }, [role, selectedGroomId, selectedBrideId, groomPhotos, bridePhotos])

  /** 선택된 참조 사진 ID 수집 */
  const getSelectedPhotoIds = useCallback(() => {
    const ids: string[] = []
    if ((role === 'GROOM' || role === 'COUPLE') && selectedGroomId) ids.push(selectedGroomId)
    if ((role === 'BRIDE' || role === 'COUPLE') && selectedBrideId) ids.push(selectedBrideId)
    return ids
  }, [role, selectedGroomId, selectedBrideId])

  const reset = useCallback(() => {
    setRole(defaultRole)
    setSelectedGroomId(null)
    setSelectedBrideId(null)
    setBlockSelection({})
  }, [defaultRole])

  return {
    role, setRole,
    selectedGroomId, setSelectedGroomId,
    selectedBrideId, setSelectedBrideId,
    blockSelection, setBlockSelection,
    hasGroom, hasBride,
    groomPhotos, bridePhotos,
    upload, uploading,
    isFormValid, isRefValid, isBlocksValid,
    getSelectedImageUrls,
    getSelectedPhotoIds,
    reset,
  }
}
